import React, { useContext, useState } from 'react'
import { t } from '../../../i18n/i18n'
import { Menu, MenuTrigger, Button, mergeClasses, MenuPopover, MenuList, MenuItem, tokens, makeStyles } from '@fluentui/react-components'
import { ChevronDown16Filled, Organization24Regular } from '@fluentui/react-icons'
import { ModalInvoiceDetails } from '../../organisation/ModalInvoiceDetails'
import { ModalOrganisationDetails } from '../../organisation/ModalOrganisationDetails'
import { ModalSwitchOrganisation } from '../../organisation/ModalSwitchOrganisation'
import { DataContext } from '../../../context/DataContext'
import { RoleGuard } from '../other/RoleGuard'
import { Components, RiskManagementModules, Role } from '../../../api/schemas/schema'
import { ModalSubscriptionDetails } from '../../organisation/subscription/ModalSubscriptionDetails'
import { ModalWorkplace } from '../../settings/ModalWorkplace'
import { ModalProtectedAssetGoal } from '../../settings/ModalProtectedAssetGoal'
import { ModalProcessOwner } from '../../settings/ModalProcessOwner'
import { ModuleGuard } from '../other/ModuleGuard'

const useStyles = makeStyles({
   colorWhite: {
      color: tokens.colorNeutralForegroundInverted,
      '&:hover': {
         color: tokens.colorBrandForegroundInvertedHover,
      },
   },
   noneInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   showOnPhone: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'block',
      },
   },
   pointer: {
      cursor: 'pointer',
   },
})

export const OrganisationMenu = () => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { organisation } = dataContext.state
   const [modals, setModals] = useState({
      protectedAsset: false,
      organisationGoal: false,
      workplace: false,
      processOwners: false,
      orgDetails: false,
      subscriptionDetails: false,
      invoiceDetails: false,
      switchOrg: false,
   })

   if (!organisation) return null

   const toggleModal = (modalName: string, isOpen: boolean) => {
      setModals({
         protectedAsset: false,
         organisationGoal: false,
         workplace: false,
         processOwners: false,
         orgDetails: false,
         subscriptionDetails: false,
         invoiceDetails: false,
         switchOrg: false,
         [modalName]: isOpen,
      })
   }

   const closeNav = () => {
      dataContext.setRootState((prev) => ({
         ...prev,
         navigationExpanded: false,
      }))
   }

   const menuList = () => {
      return (
         <MenuList>
            <ModuleGuard allowedModules={[Components.DEMAND_COMPLIANCE]}>
               <MenuItem onClick={() => toggleModal('processOwners', true)}>{t('ProcessOwner')}</MenuItem>
            </ModuleGuard>

            <ModuleGuard allowedModules={[RiskManagementModules.SIMPLIFIED_RISK_ANALYSIS, RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS]}>
               <MenuItem onClick={() => toggleModal('organisationGoal', true)}>{t('OrganisationalGoals')}</MenuItem>
               <MenuItem onClick={() => toggleModal('protectedAsset', true)}>{t('ProtectedAssets')}</MenuItem>
            </ModuleGuard>

            {organisation.workplaceLimit > 0 && <MenuItem onClick={() => toggleModal('workplace', true)}>{t('Workplaces')}</MenuItem>}

            <RoleGuard allowedRoles={[Role.SystemAdministrator]}>
               <MenuItem onClick={() => toggleModal('subscriptionDetails', true)}>{t('ModulesAndUsers')}</MenuItem>
               <MenuItem onClick={() => toggleModal('invoiceDetails', true)}>{t('InvoiceDetails')}</MenuItem>
            </RoleGuard>

            <MenuItem onClick={() => toggleModal('orgDetails', true)}>{t('OrganisationDetails')}</MenuItem>
            <MenuItem onClick={() => toggleModal('switchOrg', true)}>{t('SwitchOrganisation')}</MenuItem>
         </MenuList>
      )
   }

   return (
      <div onClick={closeNav}>
         {/* Desktop menu */}
         <Menu>
            <MenuTrigger>
               <Button
                  className={mergeClasses(classes.noneInPhone, classes.colorWhite, classes.pointer)}
                  appearance="transparent"
                  icon={<ChevronDown16Filled />}
                  iconPosition="after"
               >
                  {organisation.name}
               </Button>
            </MenuTrigger>
            <MenuPopover>{menuList()}</MenuPopover>
         </Menu>

         {/* Phone menu */}
         <Menu>
            <MenuTrigger>
               <Button
                  className={mergeClasses(classes.showOnPhone, classes.colorWhite, classes.pointer)}
                  appearance="transparent"
                  icon={<Organization24Regular />}
               >
                  <ChevronDown16Filled />
               </Button>
            </MenuTrigger>
            <MenuPopover>{menuList()}</MenuPopover>
         </Menu>

         <ModalOrganisationDetails
            open={modals.orgDetails}
            closeModal={() => {
               toggleModal('orgDetails', false)
            }}
         />

         <RoleGuard allowedRoles={[Role.SystemAdministrator]}>
            <ModalSubscriptionDetails
               open={modals.subscriptionDetails}
               closeModal={() => {
                  toggleModal('subscriptionDetails', false)
               }}
            />
            <ModalInvoiceDetails
               open={modals.invoiceDetails}
               closeModal={() => {
                  toggleModal('invoiceDetails', false)
               }}
            />
         </RoleGuard>

         <ModalSwitchOrganisation
            open={modals.switchOrg}
            closeModal={() => {
               toggleModal('switchOrg', false)
            }}
         />

         {organisation.workplaceLimit > 0 && (
            <ModalWorkplace
               open={modals.workplace}
               closeModal={() => {
                  toggleModal('workplace', false)
               }}
            />
         )}

         <ModuleGuard allowedModules={[RiskManagementModules.SIMPLIFIED_RISK_ANALYSIS, RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS]}>
            <ModalProtectedAssetGoal
               open={modals.protectedAsset}
               closeModal={() => {
                  toggleModal('protectedAsset', false)
               }}
               isProtectedAsset={true}
            />

            <ModalProtectedAssetGoal
               open={modals.organisationGoal}
               closeModal={() => {
                  toggleModal('organisationGoal', false)
               }}
               isProtectedAsset={false}
            />
         </ModuleGuard>

            <ModuleGuard allowedModules={[Components.DEMAND_COMPLIANCE]}>
            <ModalProcessOwner
               open={modals.processOwners}
               closeModal={() => {
                  toggleModal('processOwners', false)
               }}
            />
         </ModuleGuard>
      </div>
   )
}
